<script setup lang="ts">
import { TrashIcon } from "@voltalis/ui-kit/icons";
import { computed, ref } from "vue";

import { useDeleteSiteSectionMutation } from "@/composables/kili/sections/mutations/use-delete-site-section-mutation";
import { useGetSectionIsDeletableQuery } from "@/composables/kili/sections/queries/use-get-section-is-deletable-query";
import { useToaster } from "@/composables/layout/use-toaster";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { SiteSectionId, V4HSiteId } from "@/data/common/ids";
import { SectionNode } from "@/domains/v4b/site-section/site-section.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import ConfirmationDialog from "@/components/ui/dialog/confirmation-dialog.vue";

interface IProps {
  siteId: V4HSiteId;
  section: SectionNode | undefined;
}

interface IEmits {
  (e: "deletion-completed"): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const { t, k } = useTypedIntl();
const toast = useToaster();
const computedSiteId = computed<V4HSiteId>(() => props.siteId);
const maybeSelectedSectionId = computed<SiteSectionId | undefined>(() => props.section?.id);

const { data: sectionDeletable, isFetching: isFetchingSectionDeletable } = useGetSectionIsDeletableQuery(
  computedSiteId,
  maybeSelectedSectionId,
);
const { mutateAsync: deleteSiteSection, isPending: isDeletingSiteSection } = useDeleteSiteSectionMutation(
  computedSiteId,
  maybeSelectedSectionId,
);

const isSectionDeletable = computed<boolean>(() => sectionDeletable.value?.isDeletable === true);

const isDeleteSectionConfirmationDialogVisible = ref<boolean>(false);

async function handleConfirmDeleteSection() {
  try {
    await deleteSiteSection();

    toast.add({
      severity: "success",
      detail: t(k.tools.siteManagement.sectionDetails.deletion.success),
    });

    isDeleteSectionConfirmationDialogVisible.value = false;

    MixpanelService.trackEvent(EMixpanelEvent.SECTION_DELETED);

    emits("deletion-completed");
  } catch {
    toast.add({
      severity: "error",
      detail: t(k.tools.siteManagement.sectionDetails.deletion.error),
    });
  }
}
</script>

<template>
  <div class="flex flex-col gap-6 px-6 pb-6">
    <HospitalityButton
      :class="{ '!text-danger': isSectionDeletable }"
      :data-test-id="DATA_TEST_IDS.components.sectionDetailsDrawer.deleteSectionButton"
      :disabled="isFetchingSectionDeletable || isDeletingSiteSection || !isSectionDeletable"
      :icon="TrashIcon"
      severity="link"
      size="sm"
      @click="isDeleteSectionConfirmationDialogVisible = true"
    >
      {{ t(k.tools.siteManagement.sectionDetails.deletion.deleteSection) }}
    </HospitalityButton>

    <div
      v-if="!isSectionDeletable && !isFetchingSectionDeletable"
      class="flex gap-2 rounded-lg bg-surface-inverse p-3 text-xs font-semibold text-text-secondary"
      :data-test-id="DATA_TEST_IDS.components.sectionDetailsDrawer.cannotDeleteSectionText"
    >
      <i class="pi pi-info-circle mt-0.5" />
      <span> {{ t(k.tools.siteManagement.sectionDetails.deletion.cannotDeleteSectionExplanation) }}</span>
    </div>
  </div>

  <ConfirmationDialog
    id="delete-section-confirmation-dialog"
    v-model="isDeleteSectionConfirmationDialogVisible"
    :callback="() => handleConfirmDeleteSection()"
    :subtitle="t(k.global.misc.permanentAction)"
    :title="
      t(k.tools.siteManagement.sectionDetails.deletion.confirmation.title({ sectionName: props.section?.name ?? '' }))
    "
    delete-mode
    :submit-text="t(k.tools.siteManagement.sectionDetails.deletion.confirmation.submitText)"
  />
</template>
